/**
 * Simple i18n (internationalization) utility for vanilla JavaScript
 * Supports loading JSON translation files and updating DOM elements
 */
class I18n {
  constructor() {
    this.currentLanguage = "it"; // Default to Italian
    this.supportedLanguages = ["it", "en"];
    this.translations = {};
    this.fallbackLanguage = "en";
    this.isLoading = true;

    // Detect browser language and try to set it
    this.detectBrowserLanguage();

    // Initialize
    this.init();
  }

  detectBrowserLanguage() {
    const browserLang = navigator.language || navigator.userLanguage;
    const langCode = browserLang.split("-")[0]; // Get just the language part (e.g., 'en' from 'en-US')

    // Check if the detected language is supported
    if (this.supportedLanguages.includes(langCode)) {
      this.currentLanguage = langCode;
    }

    // Check localStorage for previously saved language preference
    const savedLang = localStorage.getItem("preferred-language");
    if (savedLang && this.supportedLanguages.includes(savedLang)) {
      this.currentLanguage = savedLang;
    }
  }

  async init() {
    try {
      // Load all translation files
      await this.loadTranslations();

      // Update the DOM with current language
      this.updateDOM();

      // Update page title
      // this.updateTitle();

      this.isLoading = false;

      console.log(`i18n initialized with language: ${this.currentLanguage}`);
    } catch (error) {
      console.error("Failed to initialize i18n:", error);
      this.isLoading = false;
    }
  }

  async loadTranslations() {
    const promises = this.supportedLanguages.map(async (lang) => {
      try {
        const response = await fetch(`./translations/${lang}.json`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const translations = await response.json();
        this.translations[lang] = translations;
      } catch (error) {
        console.error(`Failed to load translations for ${lang}:`, error);
        // If this is the fallback language, create empty object to prevent errors
        if (lang === this.fallbackLanguage) {
          this.translations[lang] = {};
        }
      }
    });

    await Promise.all(promises);
  }

  /**
   * Get translation for a given key path (e.g., 'header.title')
   * @param {string} keyPath - Dot-separated path to translation key
   * @param {object} variables - Optional variables for string interpolation
   * @returns {string} - Translated text
   */
  t(keyPath, variables = {}) {
    const keys = keyPath.split(".");
    let translation = this.translations[this.currentLanguage];

    // Navigate through the nested object
    for (const key of keys) {
      if (
        translation &&
        typeof translation === "object" &&
        key in translation
      ) {
        translation = translation[key];
      } else {
        // Fallback to default language
        translation = this.translations[this.fallbackLanguage];
        for (const fallbackKey of keys) {
          if (
            translation &&
            typeof translation === "object" &&
            fallbackKey in translation
          ) {
            translation = translation[fallbackKey];
          } else {
            translation = keyPath; // Return the key path if no translation found
            break;
          }
        }
        break;
      }
    }

    // Handle string interpolation
    if (typeof translation === "string" && Object.keys(variables).length > 0) {
      return translation.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        return variables[key] || match;
      });
    }

    return translation || keyPath;
  }

  /**
   * Update all DOM elements that have data-i18n attributes
   */
  updateDOM() {
    // Update elements with data-i18n attribute
    const elements = document.querySelectorAll("[data-i18n]");
    elements.forEach((element) => {
      const keyPath = element.getAttribute("data-i18n");
      const translation = this.t(keyPath);

      // Check if element has data-i18n-attr to update specific attribute
      const attrName = element.getAttribute("data-i18n-attr");
      if (attrName) {
        element.setAttribute(attrName, translation);
      } else {
        // Update innerHTML (allows for HTML in translations like <br> tags)
        element.innerHTML = translation;
      }
    });

    // Update placeholders
    const placeholderElements = document.querySelectorAll(
      "[data-i18n-placeholder]"
    );
    placeholderElements.forEach((element) => {
      const keyPath = element.getAttribute("data-i18n-placeholder");
      const translation = this.t(keyPath);
      element.setAttribute("placeholder", translation);
    });

    // Update form labels
    const labelElements = document.querySelectorAll("label[data-i18n-for]");
    labelElements.forEach((element) => {
      const keyPath = element.getAttribute("data-i18n-for");
      const translation = this.t(keyPath);
      element.textContent = translation;
    });
  }

  /**
   * Update page title
   */
  updateTitle() {
    const title = this.t("meta.title");
    if (title) {
      document.title = title;
    }
  }

  /**
   * Switch to a different language
   * @param {string} language - Language code to switch to
   */
  async switchLanguage(language) {
    if (!this.supportedLanguages.includes(language)) {
      console.warn(`Language ${language} is not supported`);
      return;
    }

    this.isLoading = true;
    this.currentLanguage = language;

    // Save preference to localStorage
    localStorage.setItem("preferred-language", language);

    // Update DOM
    this.updateDOM();
    this.updateTitle();

    this.isLoading = false;

    // Dispatch custom event for other parts of the app to listen to
    const event = new CustomEvent("languageChanged", {
      detail: { language },
    });
    document.dispatchEvent(event);

    console.log(`Language switched to: ${language}`);
  }

  /**
   * Check if translations are currently being loaded
   * @returns {boolean} True if loading, false otherwise
   */
  isTranslationLoading() {
    return this.isLoading;
  }

  /**
   * Get current language
   * @returns {string} Current language code
   */
  getCurrentLanguage() {
    return this.currentLanguage;
  }

  /**
   * Get supported languages
   * @returns {array} Array of supported language codes
   */
  getSupportedLanguages() {
    return this.supportedLanguages;
  }

  /**
   * Get language name in its native form
   * @param {string} langCode - Language code
   * @returns {string} Native language name
   */
  getLanguageName(langCode) {
    const languageNames = {
      it: "Italiano",
      en: "English",
    };
    return languageNames[langCode] || langCode;
  }
}

// Create global instance
window.i18n = new I18n();

// ES6 module export
export default I18n;

// Also export the instance for convenience
export { I18n };
